/* eslint-disable no-underscore-dangle */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import clsx from 'clsx';
import { Grid, Typography, Chip, Divider, Tooltip } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';

import '@/utility/jquery';

// CONSTANTS
import { LOCATIONS_WP_POST_PAGE } from '@/constants/locations';

// UTILITY
import { camelToSnakeCase, convertHTMLEntity, removeHTMLEntity } from '@/utility/common';
import { getLink, routeEnhance } from '@/utility/routes';
import {
  externalLink,
  analyticsPageLoaded,
  analyticsInternalLinks,
  customAnalyticsEvent,
  analyticsShareClick,
} from '@/utility/analytics';
import { getOrgSchema } from '@/utility/orgSchemas/orgSchemas';
import {
  siteUrl,
  wpComposerPublicPath,
  wpComposerAssetsPath,
  mainComposerScripts,
  runComposer,
  removeScripts,
  removeCss,
  manageClickListeners,
  WPData,
  connectScripts,
  getAPIUrlsByType,
  getLinksByType,
} from '@/utility/wp';

// STORE
import { AuthActions, WPBlogActions } from '@/store/actions';

// HOC
import Layout from '@/hoc/Layout';
import withRouter from '@/hoc/withRouter';

// COMPONENTS
import ReactHtml from '@/components/ReactHtml';
import RouterBreadcrumbs from '@/components/RouterBreadcrumbs';
import Loader from '@/components/Loader';
import Share from '@/components/Share';
import InsightCard from '@/components/Cards/InsightCard';
import AuthorInfo from '@/components/WP/AuthorInfo';
import FacebookColoredIcon from '@/components/Icons/FacebookColoredIcon';

// HELP
import DialogShowExitIntent from './help/DialogShowExitIntent';

// STYLES
import { Disclosure } from '@/styles/wp';
import { Section, WPContent } from './styles';

const wpData = new WPData();

class WPPost extends Component {
  state = {
    mainCssLoaded: false,
  };

  componentDidMount() {
    this.getPost();

    window.prerendercloudReady = false;
  }

  componentDidUpdate(prevProps, prevState) {
    const { mainCssLoaded } = this.state;
    const {
      data,
      getWPRelatedPosts,
      router: {
        params: { slug },
      },
      type,
      isAuth,
    } = this.props;
    const apiURLs = getAPIUrlsByType(type);
    const dataLength = Object.keys(data).length;

    if (prevState.mainCssLoaded !== mainCssLoaded && mainCssLoaded) {
      const externalLinks = document.querySelectorAll('a.wp-external-link');
      const authButtons = document.querySelectorAll('.auth-dialog button');

      manageClickListeners(externalLinks, 'add', this.handleExternalWPLink);
      manageClickListeners(authButtons, 'add', this.handleOpenAuthDialog);
    }

    if (prevProps.data !== data && dataLength) {
      if (data?.x_metadata?.vcvSourceAssetsFiles?.jsBundles) {
        connectScripts(data.x_metadata.vcvSourceAssetsFiles.jsBundles, wpData);
      }

      if (data?.x_metadata?.vcvSourceCssFileUrl) {
        this.connectCss(wpComposerAssetsPath + data.x_metadata.vcvSourceCssFileUrl);
      } else {
        this.setState({ mainCssLoaded: true });
      }

      const params = {
        author: data.author,
        exclude: data.id,
      };

      getWPRelatedPosts({ url: apiURLs.post, params });
    }

    if (prevProps.router.params.slug !== slug || prevProps.isAuth !== isAuth) {
      this.removeWPFromDOM();
      this.setState({ mainCssLoaded: false }, this.getPost);
    }
  }

  componentWillUnmount() {
    this.removeWPFromDOM();
  }

  getPost = () => {
    const {
      router: {
        params: { slug },
      },
      type,
      getWPPost,
    } = this.props;
    const apiURLs = getAPIUrlsByType(type);

    getWPPost(slug, apiURLs.post);

    connectScripts(mainComposerScripts, wpData);
  };

  handleExternalWPLink = e => {
    let dataset = e.currentTarget?.dataset || {};
    dataset = JSON.stringify(dataset);
    dataset = JSON.parse(dataset);

    Object.keys(dataset)
      .map(key => {
        const newKey = camelToSnakeCase(key);
        const value = dataset[key];
        delete dataset[key];
        dataset[newKey] = value;

        return newKey;
      })
      .forEach(key => {
        if (dataset[key] === 'true' || dataset[key] === 'false') {
          dataset[key] = JSON.parse(dataset[key]);
        }
      });

    const property = {
      location: 'WP Post',
      pageTitle: document.title,
      href: e.currentTarget?.href || '',
      ...dataset,
    };

    externalLink(property);
  };

  removeWPFromDOM = () => {
    const { data } = this.props;
    let scripts = mainComposerScripts;

    const externalLinks = document.querySelectorAll('a.wp-external-link');
    const authButtons = document.querySelectorAll('.auth-dialog button');

    manageClickListeners(externalLinks, 'remove', this.handleExternalWPLink);
    manageClickListeners(authButtons, 'remove', this.handleOpenAuthDialog);

    if (data?.x_metadata?.vcvSourceAssetsFiles?.jsBundles) {
      scripts = [...scripts, ...data.x_metadata.vcvSourceAssetsFiles.jsBundles];
    }

    removeScripts(scripts, wpData);

    if (data?.x_metadata?.vcvSourceCssFileUrl) {
      removeCss(data.x_metadata.vcvSourceCssFileUrl);
    }
  };

  connectCss = path => {
    const head = document.getElementsByTagName('head')[0];
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = path;

    head.appendChild(link);

    link.onload = () => this.setState({ mainCssLoaded: true }, () => runComposer(wpData));
    link.onerror = () => this.setState({ mainCssLoaded: true }, () => runComposer(wpData));
  };

  getCss = cssBundles => {
    return cssBundles.map((cssSrc, index) => {
      let curLink = cssSrc;
      const stringArray = curLink.split('/');
      const hasHTTP = stringArray[0].includes('http');

      if (!hasHTTP) {
        if (curLink.includes('sharedLibraries')) {
          curLink = `${wpComposerAssetsPath}/${curLink}`;
        } else {
          curLink = `${wpComposerPublicPath}/sources/${curLink}`;
        }
      }

      return <link rel="stylesheet" href={curLink} media="all" key={index} />;
    });
  };

  getMeta = data => {
    const metaTags = data.reduce((acc, meta, index) => {
      if (meta.name !== 'robots' && meta.property !== 'og:url') {
        acc.push(<meta key={index} {...meta} />);
      }

      return acc;
    }, []);

    return metaTags.length ? metaTags : null;
  };

  handleShareClick = (whichone, url) => {
    const { data } = this.props;

    analyticsShareClick({
      type: whichone,
      location: LOCATIONS_WP_POST_PAGE,
      url,
      id_of_item: data.id,
      slug: data?.slug,
    });
  };

  handlePopupShow = () => {
    const { data, unique_id, anonymousId, partner } = this.props;

    customAnalyticsEvent('exit popup was shown', {
      unique_id: unique_id || anonymousId,
      post_id: data.id,
      popup_type: data.exit_popup,
      partner: partner.partner_name,
    });
  };

  handleCategoryClick = slug => {
    const {
      router: { navigate },
      type,
    } = this.props;
    const links = getLinksByType(type);
    const routeEnhanced = routeEnhance(getLink(links.category, { slug }), {
      state: { location: LOCATIONS_WP_POST_PAGE },
    });

    analyticsInternalLinks({
      unique_id: routeEnhanced.state.unique_id,
      from_page_path: routeEnhanced.state.from_page_path,
      from_search_params: routeEnhanced.state.from_search_params,
      to_page_path: routeEnhanced.pathname,
      to_search_params: routeEnhanced.search,
      partner_id: routeEnhanced.state.partner_id,
      location: routeEnhanced.state.location,
    });

    navigate(routeEnhanced);
  };

  handleChangeHelmetData = () => {
    if (this.lastPath !== window.location.pathname) {
      analyticsPageLoaded();
      this.lastPath = window.location.pathname;

      window.prerendercloudReady = true;
    }
  };

  handleOpenAuthDialog = event => {
    const { manageAuthDialogState, partner } = this.props;
    const parentEl = event.target?.closest('.auth-dialog');
    const hasPartner = !!partner.slug;
    const partnerSettings = partner.settings || {};
    let useAuthorizing = true;
    let screen = 'registration';

    if (hasPartner) {
      useAuthorizing = !!partnerSettings.use_authorizing;
    }

    if (parentEl) {
      if (parentEl.classList.contains('login')) screen = 'login';
    }

    if (useAuthorizing) manageAuthDialogState({ screen });
  };

  render() {
    const { mainCssLoaded } = this.state;
    const {
      t,
      router: {
        params: { slug },
      },
      type,
      isAuth,
      data,
      loading,
      relatedPosts,
      loadingRelatedPosts,
      isPartnerDomain,
      partnerSlug,
      partner,
      partnerLoading,
    } = this.props;
    const links = getLinksByType(type);
    const isPartner = isPartnerDomain || !!partnerSlug;
    const shareLink = window.location.href;
    const hasData = !!Object.keys(data).length;

    const facebookLink = `https://www.facebook.com/sharer/sharer.php?u=${shareLink}`;
    const twitterLink = `https://twitter.com/intent/tweet?text=${t('WPPost.share.twitter', {
      post: data?.title?.rendered || '',
    })}&url=${shareLink}&related=mybundletv&hashtags=cutthecord,mybundletv`;
    const shareData = [
      {
        link: facebookLink,
        icon: <FacebookColoredIcon />,
        title: 'Facebook',
        onClick: () => this.handleShareClick('facebook', facebookLink),
        id: 'fbShareWindow',
      },
      {
        link: twitterLink,
        icon: 'x',
        title: 'X',
        onClick: () => this.handleShareClick('twitter', twitterLink),
        id: 'twShareWindow',
      },
    ];

    const seoData = {
      canonicalUrl: `${siteUrl}${getLink(links.post, { slug })}`,
      ogUrl: isPartner
        ? `${siteUrl}${getLink(links.partnerPost, { partner: partner.slug, slug })}`
        : `${siteUrl}${getLink(links.post, { slug })}`,
    };

    return (
      <Layout>
        {!loading && hasData && !partnerLoading && mainCssLoaded && (
          <Helmet
            onChangeClientState={this.handleChangeHelmetData}
            script={[getOrgSchema('wpPost', data)]}
          >
            {data?.yoast_title && <title>{convertHTMLEntity(data.yoast_title)}</title>}
            <link
              rel="stylesheet"
              href={`${wpComposerPublicPath}/dist/front.bundle.css`}
              media="all"
            />
            <link rel="canonical" href={seoData.canonicalUrl} />
            <meta property="og:url" content={seoData.ogUrl} />
            {data?.x_metadata?.vcvSourceAssetsFiles?.cssBundles &&
              this.getCss(data.x_metadata.vcvSourceAssetsFiles.cssBundles)}
            {data?.x_featured_media_medium && (
              <meta name="og:image" content={data.x_featured_media_medium} />
            )}
            {data?.x_featured_media_medium && (
              <meta name="twitter:image" content={data.x_featured_media_medium} />
            )}
            <meta name="twitter:title" content={convertHTMLEntity(data.yoast_title)} />
            {data?.excerpt?.rendered && (
              <meta
                name="twitter:description"
                content={`MyBundle.TV - ${removeHTMLEntity(data.excerpt.rendered)}`}
              />
            )}
            <meta name="twitter:site" content="@mybundletv" />
            <meta name="twitter:creator" content="@mybundletv" />
            <meta name="twitter:card" content="summary" />
            {data?.yoast_meta && this.getMeta(data.yoast_meta)}
          </Helmet>
        )}
        <div className="page-content">
          <RouterBreadcrumbs lastBreadcrumbText={data?.title?.rendered || ''} />
          {!!data.affiliate_disclosure && data.type !== 'news' && (
            <Tooltip
              title={<Typography className="p4">{t('advertiserDisclosure.disclosure')}</Typography>}
              placement="bottom-start"
            >
              <Disclosure color="textSecondary">
                <InfoIcon />
                {t('advertiserDisclosure.title')}
              </Disclosure>
            </Tooltip>
          )}
        </div>
        <Section className="page-content">
          <div className="post-header">
            {data?._embedded && data?._embedded['wp:term'] && data?._embedded['wp:term'][0] && (
              <Grid container spacing={1} justifyContent="center" className="m-b-15">
                {data?._embedded['wp:term'][0].map(category => (
                  <Grid item key={category.id}>
                    <Chip
                      label={category.name}
                      onClick={() => this.handleCategoryClick(category.slug)}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
            <Typography component="h1" variant="h3" align="center" className="post-header__title">
              {data?.title?.rendered || ''}
            </Typography>
            <div className="post-header__author">
              {!!data?._embedded?.author?.length && data?.x_date && (
                <AuthorInfo
                  data={data?._embedded.author[0]}
                  date={data.x_date}
                  type={type}
                  locations={LOCATIONS_WP_POST_PAGE}
                  hideAuthor={type === 'news'}
                />
              )}
            </div>
          </div>
          <WPContent
            className={clsx('post-page', isAuth && 'auth', isPartner ? 'partner' : 'regular-mb')}
          >
            {hasData && data?.content?.rendered && mainCssLoaded && (
              <ReactHtml className="markdown">{data.content.rendered.trim()}</ReactHtml>
            )}
          </WPContent>
          <div className="post-share">
            <Divider />
            <div className="post-share__content">
              <Share
                data={shareData}
                copyLink={shareLink}
                black
                clickCopylink={() => this.handleShareClick('Copy Link', shareLink)}
              />
            </div>
            <Divider />
          </div>
          {(loadingRelatedPosts || !!relatedPosts.length) && (
            <div className="post-related-posts">
              <Grid container spacing={4}>
                {relatedPosts.map(post => (
                  <Grid item xs={12} sm={4} key={post.id}>
                    <InsightCard
                      data={post}
                      type={type}
                      location={LOCATIONS_WP_POST_PAGE}
                      hideAuthor={type === 'news'}
                    />
                  </Grid>
                ))}
              </Grid>
              <Loader active={loadingRelatedPosts} />
            </div>
          )}
        </Section>
        <Loader
          fullFilled
          active={loading || !mainCssLoaded}
          text={t(`${type === 'news' ? 'newsPage' : 'insightsPage'}.loaders.article`)}
        />
        {data.id && (
          <DialogShowExitIntent id={data.id} type={data.exit_popup} onOpen={this.handlePopupShow} />
        )}
      </Layout>
    );
  }
}

WPPost.defaultProps = {
  type: 'blog',
};

WPPost.propTypes = {
  t: PropTypes.func.isRequired,
  router: PropTypes.object.isRequired,
  isAuth: PropTypes.bool.isRequired,
  anonymousId: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  relatedPosts: PropTypes.array.isRequired,
  loadingRelatedPosts: PropTypes.bool.isRequired,
  getWPPost: PropTypes.func.isRequired,
  getWPRelatedPosts: PropTypes.func.isRequired,
  partner: PropTypes.object,
  partnerLoading: PropTypes.bool.isRequired,
  unique_id: PropTypes.string,
  manageAuthDialogState: PropTypes.func.isRequired,
  isPartnerDomain: PropTypes.bool.isRequired,
  partnerSlug: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['blog', 'news']),
};

const mapStateToProps = ({ app, wpBlog, partner, auth }) => ({
  isAuth: auth.isAuth,
  anonymousId: auth.anonymousId,
  data: wpBlog.postData,
  loading: wpBlog.loadings.main,
  relatedPosts: wpBlog.relatedPosts,
  loadingRelatedPosts: wpBlog.loadings.relatedPosts,
  unique_id: auth.unique_id,
  partner: partner.data,
  partnerLoading: partner.loadings.partner,
  isPartnerDomain: partner.isPartnerDomain,
  partnerSlug: app.partner,
});

const mapDispatchToProps = dispatch => {
  return {
    getWPPost: (slug, url) => dispatch(WPBlogActions.getWPPost.action({ slug, url })),
    getWPRelatedPosts: data => dispatch(WPBlogActions.getWPRelatedPosts.action(data)),
    manageAuthDialogState: props =>
      dispatch(AuthActions.manageAuthDialogState.action({ state: true, props })),
  };
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withTranslation(),
  withRouter,
)(WPPost);
